import { useState } from "react"
import PropTypes from "prop-types"
import { columnsPropTypes, rowPropTypes } from "components/ui/Table/tablePropsTypes"
import Icon from "components/planning_center_icon"
import TableCell from "components/ui/TableCell"
import { Checkbox } from "components/ui"

const BaseTableRow = ({
  columns,
  row,
  provided,
  snapshot,
  draggableType,
  className,
  style,
  onRowClick,
  index,
  isSelectable,
  onSelectChange,
  selectableField = "selected",
  isDeletable,
  onDeleteClick
}) => {
  const [selected, setSelected] = useState(row[selectableField])

  const handleChange = e => {
    setSelected(e.target.checked)
    return onSelectChange && onSelectChange(row, e)
  }

  const handleKeyClick = e => {
    if (
      e.key === "Enter" ||
      e.key === " " ||
      e.key === "Spacebar" ||
      e.which === 13 ||
      e.which === 32
    ) {
      e.preventDefault()
      handleRowClick(e)
    }
  }

  const handleRowClick = e => {
    if (["input", "label"].includes(e.target.localName)) {
      return
    }

    return onRowClick && onRowClick(row, e)
  }

  const providedProps = {
    ...(provided && { ref: provided.innerRef }),
    ...(provided && { ...provided.draggableProps }),
    ...(provided && draggableType === "row" && { ...provided.dragHandleProps })
  }

  return (
    <tr
      className={`${!row.isClickDisabled && onRowClick && "row__clickable"} ${className}`}
      style={style}
      tabIndex="0"
      css={row.css}
      role={!row.isClickDisabled && onRowClick ? "button" : "row"}
      {...providedProps}
      onClick={row.isClickDisabled ? null : handleRowClick}
      onKeyPress={row.isClickDisabled ? null : handleKeyClick}
    >
      {provided && (
        <td
          style={{
            cursor: "grab",
            padding: 0,
            display: row.render ? "none" : "table-cell"
          }}
          {...(draggableType === "handle" && provided.dragHandleProps)}
        >
          <div style={{ padding: "1rem" }}>
            <Icon className="c-neutral-300" symbol="general#three-horizontal-bars" />
          </div>
        </td>
      )}

      {isSelectable && (
        <td
          css={{
            verticalAlign: "middle",
            padding: 0,
            "& .checkbox-label": {
              top: 0,
              padding: "1.5rem"
            },
            "& .checkbox-label:before": {
              top: "50%",
              marginTop: -8,
              left: "50%",
              marginLeft: -8
            },
            "& .checkbox-label:after": {
              top: "50%",
              marginTop: -5,
              left: "50%",
              marginLeft: -3
            }
          }}
        >
          <Checkbox
            name={`selectable-${row.id || row.key || index}`}
            htmlFor={`selectable-${row.id || row.key || index}`}
            value={`${row.id || row.key || index}`}
            onSelect={handleChange}
            checked={selected}
            wrapperClasses=""
          />
        </td>
      )}

      {row.render
        ? row.render(row, columns)
        : columns.map(column => (
            <TableCell
              key={column.cell}
              column={column}
              row={row}
              isDragging={snapshot?.isDragging}
            />
          ))}
      {isDeletable && (
        <td className="p-0">
          <button
            className="delete p-2 c-neutral-300"
            style={{
              cursor: "pointer"
            }}
            onClick={() => onDeleteClick(row.id)}
          >
            <Icon symbol="general#trash" aria-label="Close Modal" />
          </button>
        </td>
      )}
    </tr>
  )
}

BaseTableRow.propTypes = {
  columns: columnsPropTypes,
  row: rowPropTypes,
  provided: PropTypes.shape({
    innerRef: PropTypes.func,
    draggableProps: PropTypes.object,
    dragHandleProps: PropTypes.object
  }),
  snapshot: PropTypes.object,
  draggableType: PropTypes.string,
  className: PropTypes.string,
  style: PropTypes.object,
  onRowClick: PropTypes.func,
  index: PropTypes.number,
  isSelectable: PropTypes.bool,
  onSelectChange: PropTypes.func,
  selectableField: PropTypes.string,
  isDeletable: PropTypes.bool,
  onDeleteClick: PropTypes.func
}

export default BaseTableRow
